<template>
    <div class="card">
        <Dialog v-model:visible="visible" :style="{ width: '40vw' }" modal :header="dialogHeader" class="p-fluid dialog-header" @hide="$emit('hide-dialog', true)">
            <!-- Selection for creation of normal of tagged attributes starts-->
            <div class="field" v-if="!editMode">
                <!-- flex flex-column justify-content-center -->
                <!-- <label for="propertyType" class="my-2 text-lg my-2">Do you want to make a normal attribute or a tagged attribute?</label> -->
                <SelectButton id="propertyType" v-model="propertyType" :options="getAttributePropertyTypes()" optionLabel="name" :disabled="propertyType ? true : false" />
            </div>
            <!-- Selection for creation of normal of tagged attributes ends-->

            <!-- Main Attribute Body starts -->
            <div v-if="propertyType">
                <!-- Only available on creation mode and for normal attributes -->
                <div class="formgrid grid justify-content-center">
                    <!-- Switch between company or shared attributes -->
                    <switch-shared v-if="authStore.isGodUser && !editMode && !isTagged" v-model="sharedType" />
                    <Divider />
                </div>
                <!-- Checks for User Profile Attribute or not || only available if you have permission to write it also make sure its not tagged attribuet -->
                <user-profile-switch v-if="authStore.hasPermission(UserPermission.WRITE_USER_PROFILE_ATTRIBUTES) && !editMode && !isTagged" v-model="attribute.user_profile" />
                <!-- Make a tagged attribute shareable or not -->
                <attribute-shareable v-if="isTagged" v-model="attribute.shared" />
                <div class="formgrid grid">
                    <!-- Attribute Tags -->
                    <attribute-tags v-if="isTagged" v-model="attribute.tags" class="field col-12 md:col-6" />
                    <div class="field col-12 md:col-6">
                        <label for="inputType">Input Type *</label>
                        <Dropdown
                            id="inputType"
                            :class="{ 'p-invalid': submitted && formErrors.type }"
                            autofocus
                            v-model="attribute.type"
                            :options="input_types"
                            optionLabel="name"
                            optionValue="value"
                            placeholder="Select Input Type"
                            :showClear="!isTagged"
                            :disabled="isTagged || editMode"
                        />
                        <small class="p-invalid" v-if="submitted && formErrors.type">{{ formErrors.type }}</small>
                    </div>
                </div>
                <div class="field">
                    <label for="label">Label</label>
                    <InputText id="label" :class="{ 'p-invalid': submitted && formErrors.label }" autofocus v-model="attribute.label" placeholder="Enter label for attribute" />
                    <small class="p-invalid" v-if="submitted && formErrors.label">{{ formErrors.label }}</small>
                </div>
                <div class="field">
                    <label for="code">Code *</label>
                    <InputText id="code" :class="{ 'p-invalid': submitted && formErrors.code }" v-model="attribute.code" placeholder="Enter unique code name. e.g abc_def.." :disabled="editMode || isTagged" />
                    <small class="p-invalid" v-if="submitted && formErrors.code">{{ formErrors.code }}</small>
                </div>
                <div class="formgrid grid">
                    <div class="col-12 md:col-6">
                        <!-- Attribute Category -->
                        <attribute-category v-model="attribute.category_id" />
                    </div>
                    <div class="col-12 md:col-6">
                        <!-- attribute icon here -->
                        <attribute-icon v-model="attribute.icon" />
                    </div>
                </div>
                <div class="field">
                    <label for="description">Description</label>
                    <Textarea id="description" rows="3" v-model="attribute.description" />
                </div>
                <div class="field" v-if="attribute.type && !textOnly">
                    <label for="placeholder">Placeholder</label>
                    <InputText id="placeholder" v-model="attribute.placeholder" placeholder="Enter a placeholder value" />
                </div>
                <div class="grid" v-if="isNumeric">
                    <div class="field col-12 md:col-6">
                        <label for="unit">Suffix</label>
                        <InputText id="unit" v-model="attribute.unit" placeholder="Enter a suffix value" :disabled="isTagged" />
                        <small class="p-invalid">(Suffix value will be the representation of the unit of the above numeric value)</small>
                    </div>
                    <div class="field col-12 md:col-6">
                        <label for="formula">Formula</label>
                        <Dropdown id="formula" v-model="attribute.formula_id" optionValue="id" optionLabel="code" :options="valueFormulas" placeholder="Select a formula" showClear />
                        <small class="p-invalid">(You can add formula to make a calculated attribute)</small>
                    </div>
                </div>
                <!-- options here -->
                <attribute-options v-if="enableOptions" v-model="attribute.options" />
                <!-- Validations -->
                <attribute-validations v-if="enableValidations" v-model="attribute.rules" :disabled="isTagged" />

                <div class="grid">
                    <div class="field col-6">
                        <label for="options">Required</label>
                        <SelectButton id="options" class="mb-2" v-model="attribute.required" :options="[true, false]" />
                    </div>
                </div>
            </div>
            <!-- Main Attribute Body ends -->
            <template v-if="propertyType" #footer>
                <!-- Warning to show attribute is public to share library -->
                <shared-warning v-if="sharedType === SharedTypes.SHARED && !isTagged" />
                <Button label="Cancel" icon="pi pi-times" class="p-button-danger" @click="$emit('hide-dialog', true)" />
                <Button label="Create" icon="pi pi-check" class="ml-2 p-button-primary" @click="submit" />
            </template>
        </Dialog>
    </div>
</template>

<script setup>
import AttributeCategory from '../components/AttributeCategory.vue';
import AttributeIcon from '../components/AttributeIcon.vue';

import { ref, computed, onMounted, onUnmounted, defineEmits, defineAsyncComponent } from 'vue';
import { storeToRefs } from 'pinia';
import { object, string } from 'yup';

import { authStore, attributeStore, formulaStore } from '../../../../../store/global-stores';
import { SharedTypes } from '../../../enum';
import { AttributePropertyTypes, AttributeTypes, AttributeSubmissionTypes } from '../enum';
import { UserPermission } from '../../../../../modules/permission/enum';
import { getAttributePropertyTypes } from '../constants';
import { handleValidationErrors } from '../../../../../utilities/form-errors-handler';
import { showToast } from '../../../../../utilities/toast-handler';

// async components
const AttributeTags = defineAsyncComponent(() => import('../components/AttributeTags.vue'));
const SwitchShared = defineAsyncComponent(() => import('../../../shared/SwitchShared.vue'));
const UserProfileSwitch = defineAsyncComponent(() => import('../components/UserProfileSwitch.vue'));
const AttributeShareable = defineAsyncComponent(() => import('../components/AttributeShareable.vue'));
const SharedWarning = defineAsyncComponent(() => import('../../../shared/SharedWarning.vue'));
const AttributeOptions = defineAsyncComponent(() => import('../components/AttributeOptions.vue'));
const AttributeValidations = defineAsyncComponent(() => import('../components/AttributeValidations.vue'));

// emit
const emit = defineEmits(['hide-dialog']);
// attribute store properties
const { attribute, input_types } = storeToRefs(attributeStore);
// formula store properties
const { valueFormulas } = storeToRefs(formulaStore);

// creation in between normal or tagged attribute
const propertyType = ref(null); //for storing property type of attribute i.e tagged or normal
// creation in between company or shared attribute (normal attribute)
const sharedType = ref(null);

// generic properties
const submitted = ref(false);
const submissionType = ref(null);
const formErrors = ref([]);
// for dialog visibility
const visible = ref(true);

const validationSchema = object({
    label: string().required('Attribute label is required'),
    code: string()
        .required('Attribute Code is required')
        .matches(/^[a-zA-Z0-9_]+$/, 'Attribute code must be alphanumeric only'), //.min(5, 'Attribute code should have atleast 5 characters'),
    type: string().required('Attribute Type is required'),
});

// computed properties starts
const dialogHeader = computed(() => {
    return editMode.value ? 'Update Attribute Details' : 'Create Custom Attribute';
});

const editMode = computed(() => {
    return attributeStore.editId !== null;
});

const isTagged = computed(() => {
    return propertyType.value?.code === AttributePropertyTypes.TAGGED;
});

const textOnly = computed(() => {
    const activeAttributeType = parseInt(attribute.value.type);
    const supportedAttributeTypes = [AttributeTypes.HEADING, AttributeTypes.SUB_HEADING, AttributeTypes.STATIC_TEXT];
    return supportedAttributeTypes.includes(activeAttributeType);
});

const isNumeric = computed(() => {
    const activeAttributeType = parseInt(attribute.value.type);
    return activeAttributeType === AttributeTypes.NUMBER;
});

const enableOptions = computed(() => {
    const activeAttributeType = parseInt(attribute.value.type);
    const supportedAttributeTypes = [AttributeTypes.DROP_DOWN, AttributeTypes.MULTI_DROP_DOWN, AttributeTypes.RADIO, AttributeTypes.CHECKBOX];
    return supportedAttributeTypes.includes(activeAttributeType);
});

const enableValidations = computed(() => {
    const activeAttributeType = parseInt(attribute.value.type);
    const supportedAttributeTypes = [AttributeTypes.INPUT_TEXT, AttributeTypes.NUMBER, AttributeTypes.LONG_INPUT_TEXT];
    return supportedAttributeTypes.includes(activeAttributeType);
});
// computed properties ends

// load stuffs when component is loaded
onMounted(() => {
    // check if store has editId active or no
    // if yes then its edit mode
    if (editMode.value) {
        const types = getAttributePropertyTypes();
        // check propertyType from attribute
        if (attribute.value.tags && attribute.value.tags.length > 0) {
            propertyType.value = types?.find((type) => type.code === AttributePropertyTypes.TAGGED);
        } else {
            propertyType.value = types?.find((type) => type.code === AttributePropertyTypes.NORMAL);
        }
    }
    loadSharedType();
});

onUnmounted(() => {
    attributeStore.initAttribute();
    propertyType.value = null;
});

const loadSharedType = () => {
    // if the form is on add mode then by default SharedType will be company
    // else if its in Edit mode then, if attribute has company_id then it will be company or else shared
    sharedType.value = !editMode.value ? SharedTypes.COMPANY : attribute.value?.company_id ? SharedTypes.COMPANY : SharedTypes.SHARED;
};

const submit = async () => {
    try {
        submitted.value = true;
        await validationSchema.validate(attribute.value, { abortEarly: false });
        // if valid
        // filter the unnecessary or stale inputs
        filterAttributeBody();
        // then submit attribute
        // check if its edit mode
        const response = editMode.value ? await attributeStore.updateAttribute(attribute.value, submissionType.value) : await attributeStore.createAttribute(attribute.value, submissionType.value);
        if (response) {
            emit('hide-dialog');
        }
    } catch (error) {
        if (error.inner && typeof error.inner === 'object') {
            showToast({}, 'Input data invalid');
            formErrors.value = handleValidationErrors(error);
        }
    }
};

const filterAttributeBody = () => {
    const attributeType = parseInt(attribute.value?.type);
    // Wherever the condition applies, the unnecessary fields are nullified or emptied
    // if attribute type is not Number, remove unit and formula_id
    if (attributeType !== AttributeTypes.NUMBER) {
        attribute.value.unit = null;
        attribute.value.formula_id = null;
    }

    // remove options if attribute type is not multi options attribute type like dropdown and so on..
    if (![AttributeTypes.DROP_DOWN, AttributeTypes.MULTI_DROP_DOWN, AttributeTypes.RADIO, AttributeTypes.CHECKBOX].includes(attributeType)) {
        attribute.value.options = [];
    }

    // remove placeholders,and other stuffs incase of static text types - Heading, Sub Heading, Free Text
    if ([AttributeTypes.HEADING, AttributeTypes.SUB_HEADING, AttributeTypes.STATIC_TEXT].includes(attributeType)) {
        attribute.value.placeholder = null;
        attribute.value.required = null;
    }

    //check if propertyType is Normal or Tagged
    // if normal then delete tags and shared keys
    if (propertyType.value.code === AttributePropertyTypes.NORMAL) {
        delete attribute.value.shared;
        delete attribute.value.tags;
    }
    // else delete the user_profile keys
    else {
        delete attribute.value.user_profile;
    }
    // setup submission type here i.e Company, Shared or Tagged
    // checks if property type is tagged else checks sharedType
    submissionType.value = propertyType.value?.code === AttributePropertyTypes.TAGGED ? AttributeSubmissionTypes.TAGGED : sharedType.value === SharedTypes.COMPANY ? AttributeSubmissionTypes.COMPANY : AttributeSubmissionTypes.SHARED;
};
</script>
